/**
 * @file Constructeurs de cibles propres au hub : arbres geants (chene, pin, saule, palmier, baobab : couronne separee du tronc, on passe dessous),
 *       decors de saison (parterre de fleurs, citrouille, tas de feuilles, bonhomme de neige) et herbe a chat. Meme systeme Shape / ProtoPart
 *       que les autres themes ; repere +Y haut, +Z = face, origine au sol.
 */
import { Euler, Quaternion, Vector3 } from 'three';
import type { TargetThemeId } from '@config/types';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import { mixHex } from './MergeBuilder';
import type { PartOptions } from './MergeBuilder';
import { DARK, LEAF, LEAF_DARK, Shape, TAU, TRUNK, WHITE } from './ThemeKit';
import type { ThemeBuilder } from './ThemeKit';

const Y_AXIS = new Vector3(0, 1, 0);
const scratchDir = new Vector3();

/** Rotation (rx, ry, rz) qui amene l'axe +Y d'une primitive (capsule, cylindre) sur la direction (dx, dy, dz). */
const aim = (dx: number, dy: number, dz: number): Pick<PartOptions, 'rx' | 'ry' | 'rz'> => {
  scratchDir.set(dx, dy, dz).normalize();
  const e = new Euler().setFromQuaternion(new Quaternion().setFromUnitVectors(Y_AXIS, scratchDir), 'XYZ');
  return { rx: e.x, ry: e.y, rz: e.z };
};

/** Segment (capsule) du point a au point b. */
const limb = (s: Shape, geo: GeometryLibrary, a: readonly [number, number, number], b: readonly [number, number, number], r: number, color: string): void => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz);
  s.add(geo.capsule(r, Math.max(0.02, len - 2 * r), 4, 8), color, { x: (a[0] + b[0]) / 2, y: (a[1] + b[1]) / 2, z: (a[2] + b[2]) / 2, ...aim(dx, dy, dz) });
};

const EYE = '#2b2017';

export const HUB_THEME_BUILDERS: Pick<Record<TargetThemeId, ThemeBuilder>, 'giantOak' | 'giantPine' | 'giantWillow' | 'giantPalm' | 'giantBaobab' | 'flowers' | 'pumpkin' | 'leaves' | 'snowman' | 'catnip'> = {
  // ------------------------------------------------------------------------------------------------------ arbres geants
  // Hauteur ~3.1-3.5 (x scale ~3.3 = 10-11 u) ; la couronne commence a >= 1.4 (au-dessus des chats, ~0.6 en unites du constructeur).

  /** Chene : fut conique a empattements, 2-3 charpentieres, couronne de 6 a 8 gros lobes (rayon ~1.25), fleurs / fruits accent. */
  giantOak: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const trunk = p.trunk ?? TRUNK;
    s.add(geo.cylinder(0.2, 0.31, 1.9, 10), trunk, { y: 0.95 });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + rand();
      s.add(geo.sphere(1, 8, 6), trunk, { x: Math.sin(a) * 0.27, y: 0.07, z: Math.cos(a) * 0.27, sx: 0.2, sy: 0.12, sz: 0.1, ry: a });
    }
    const n = 5 + (variant % 3);
    const lobes: Array<{ x: number; y: number; z: number; r: number }> = [{ x: 0, y: 2.6, z: 0, r: 0.82 }];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rand() * 0.6;
      const d = 0.42 + 0.1 * rand();
      lobes.push({ x: Math.sin(a) * d, y: 2.25 + rand() * 0.4, z: Math.cos(a) * d, r: 0.6 + 0.14 * rand() });
    }
    lobes.forEach((b, i) => s.crown(geo.ico(1, 1), i % 2 === 0 ? p.main : p.secondary, { x: b.x, y: b.y, z: b.z, s: b.r, sy: b.r * 0.88, ry: rand() * 3 }));
    for (let i = 0; i < 3; i++) {
      const b = lobes[1 + i % n];
      if (b) limb(s, geo, [0, 1.6, 0], [b.x * 0.8, b.y - 0.25, b.z * 0.8], 0.07, trunk);
    }
    for (let i = 0; i < 7; i++) {
      const b = lobes[Math.floor(rand() * lobes.length)];
      if (!b) continue;
      const th = rand() * TAU;
      const ph = 0.3 + rand() * 1.0;
      s.crown(geo.sphere(0.09, 6, 5), p.accent, { x: b.x + Math.sin(th) * Math.sin(ph) * b.r, y: b.y + Math.cos(ph) * b.r * 0.9, z: b.z + Math.cos(th) * Math.sin(ph) * b.r });
    }
    return s.finish();
  },

  /** Pin : fut nu puis 4 a 6 etages de cones decroissants (rayon bas ~0.9), pointes claires, etoile au sommet. Elance (~3.1). */
  giantPine: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.cylinder(0.14, 0.24, 1.6, 9), p.trunk ?? TRUNK, { y: 0.8 });
    const n = 4 + (variant % 3);
    const spin = rand() * 3;
    const step = 1.55 / (n - 1);
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const y = 1.15 + i * step;
      const h = 0.78 - 0.36 * t;
      const r = 0.9 * (1 - 0.7 * t) * (0.96 + 0.08 * rand());
      s.crown(geo.cone(r, h, 9), i % 2 === 0 ? p.main : p.secondary, { y: y + h / 2, ry: spin + i * 0.55 });
      for (let k = 0; k < 3; k++) {
        const a = rand() * TAU;
        s.crown(geo.sphere(0.06, 6, 5), p.accent, { x: Math.sin(a) * r * 0.78, y: y + 0.03, z: Math.cos(a) * r * 0.78 });
      }
    }
    s.crown(geo.sphere(0.11, 8, 6), p.accent, { y: 1.15 + 1.55 + 0.4 });
    return s.finish();
  },

  /** Saule pleureur : fut court fourchu, dome de lobes et rideau de 14 a 18 lanieres pendantes (bas >= 0.95), couronne ~1.3. */
  giantWillow: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const trunk = p.trunk ?? TRUNK;
    s.add(geo.cylinder(0.2, 0.32, 1.5, 10), trunk, { y: 0.75 });
    const lean = rand() * TAU;
    limb(s, geo, [0, 1.4, 0], [Math.sin(lean) * 0.3, 2.0, Math.cos(lean) * 0.3], 0.1, trunk);
    limb(s, geo, [0, 1.35, 0], [-Math.sin(lean) * 0.28, 1.95, -Math.cos(lean) * 0.28], 0.09, trunk);
    s.crown(geo.ico(1, 1), p.main, { y: 2.5, s: 0.72, sy: 0.6 });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU + rand();
      s.crown(geo.ico(1, 1), i % 2 === 0 ? p.secondary : p.main, { x: Math.sin(a) * 0.5, y: 2.3 + rand() * 0.15, z: Math.cos(a) * 0.5, s: 0.5, sy: 0.4 });
    }
    const m = 14 + (variant % 3) * 2;
    for (let i = 0; i < m; i++) {
      const a = (i / m) * TAU + rand() * 0.3;
      const rr = 0.5 + 0.62 * rand();
      const top = 2.2 + rand() * 0.15;
      const len = 1.0 + 0.4 * rand();
      s.crown(geo.cone(0.13, len, 5), i % 2 === 0 ? p.main : p.secondary, { x: Math.sin(a) * rr, y: top - len / 2, z: Math.cos(a) * rr, rx: Math.PI, rz: (rand() - 0.5) * 0.2 });
      if (i % 4 === 0) s.crown(geo.sphere(0.06, 6, 5), p.accent, { x: Math.sin(a) * rr, y: top - len, z: Math.cos(a) * rr });
    }
    return s.finish();
  },

  /** Palmier geant : fut annele leggerement courbe (~2.9), 8 a 10 frondes retombantes, coeur clair et noix. Couronne ~1.1. */
  giantPalm: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const trunk = p.trunk ?? TRUNK;
    const dir = variant % 2 === 0 ? 1 : -1;
    const bend = 0.06 + 0.04 * rand();
    const segs = 7;
    const segLen = 0.42;
    let px = 0;
    let py = 0;
    for (let i = 0; i < segs; i++) {
      const a = dir * i * bend;
      s.add(geo.cylinder(0.1 - i * 0.004, 0.14 - i * 0.005, segLen + 0.05, 8), i % 2 === 0 ? trunk : mixHex(trunk, DARK, 0.18), {
        x: px + Math.sin(a) * (segLen / 2),
        y: py + Math.cos(a) * (segLen / 2),
        rz: -a,
      });
      px += Math.sin(a) * segLen;
      py += Math.cos(a) * segLen;
    }
    const leaves = 8 + (variant % 2) * 2;
    for (let k = 0; k < leaves; k++) {
      const ang = (k / leaves) * TAU + rand() * 0.2;
      const len = 0.55 + rand() * 0.08;
      s.crown(geo.sphere(1, 10, 6), k % 2 === 0 ? p.main : p.secondary, {
        x: px + Math.sin(ang) * len * 0.8,
        y: py + 0.04 - Math.abs(Math.sin(k * 1.7)) * 0.1,
        z: Math.cos(ang) * len * 0.8,
        ry: ang,
        rx: 0.5 + rand() * 0.2,
        order: 'YXZ',
        sx: 0.17,
        sy: 0.05,
        sz: len,
      });
    }
    s.crown(geo.sphere(0.17, 8, 6), p.accent, { x: px, y: py + 0.08 });
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * TAU + 0.6;
      s.crown(geo.sphere(0.11, 8, 6), mixHex(trunk, DARK, 0.25), { x: px + Math.sin(a) * 0.17, y: py - 0.12, z: Math.cos(a) * 0.17 });
    }
    return s.finish();
  },

  /** Baobab : fut tres epais et renfle (base ~0.42, collider 0.34), branches courtes, couronne plate de 6-7 lobes ecrases (~1.2), graines pendantes. */
  giantBaobab: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const trunk = p.trunk ?? TRUNK;
    s.add(geo.cylinder(0.3, 0.42, 1.6, 12), trunk, { y: 0.8 });
    s.add(geo.sphere(1, 12, 8), mixHex(trunk, WHITE, 0.08), { y: 0.55, sx: 0.5, sy: 0.7, sz: 0.5 });
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU + rand();
      s.add(geo.sphere(1, 8, 6), trunk, { x: Math.sin(a) * 0.4, y: 0.08, z: Math.cos(a) * 0.4, sx: 0.22, sy: 0.14, sz: 0.12, ry: a });
    }
    const n = 6 + (variant % 2);
    s.crown(geo.ico(1, 1), p.main, { y: 2.3, s: 0.85, sy: 0.36 });
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rand() * 0.4;
      const d = 0.7 + 0.1 * rand();
      const x = Math.sin(a) * d;
      const z = Math.cos(a) * d;
      limb(s, geo, [0, 1.55, 0], [x * 0.85, 2.05, z * 0.85], 0.1, trunk);
      s.crown(geo.ico(1, 1), i % 2 === 0 ? p.secondary : p.main, { x, y: 2.2 + 0.12 * rand(), z, s: 0.46 + 0.08 * rand(), sy: 0.2, ry: rand() * 3 });
    }
    for (let i = 0; i < 6; i++) {
      const a = rand() * TAU;
      const d = 0.2 + 0.8 * rand();
      s.crown(geo.capsule(0.05, 0.22, 3, 6), p.accent, { x: Math.sin(a) * d, y: 1.95, z: Math.cos(a) * d });
    }
    return s.finish();
  },

  // ------------------------------------------------------------------------------------------------------ decors de saison
  /** Parterre : touffe de feuilles basses et 5 a 7 fleurs a petales (main / secondary) et coeur accent, tiges vertes. Rayon ~0.5. */
  flowers: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.sphere(1, 10, 6), LEAF, { y: 0.06, sx: 0.5, sy: 0.12, sz: 0.46 });
    const n = 5 + (variant % 3);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rand() * 0.8;
      const d = i === 0 ? 0.05 : 0.18 + 0.28 * rand();
      const x = Math.sin(a) * d;
      const z = Math.cos(a) * d;
      const h = 0.3 + 0.2 * rand();
      s.add(geo.cylinder(0.025, 0.03, h, 5), LEAF_DARK, { x, y: 0.1 + h / 2, z });
      const petal = i % 2 === 0 ? p.main : p.secondary;
      const y = 0.1 + h + 0.02;
      for (let k = 0; k < 5; k++) {
        const b = (k / 5) * TAU;
        s.add(geo.sphere(0.075, 6, 5), petal, { x: x + Math.sin(b) * 0.1, y, z: z + Math.cos(b) * 0.1, sy: 0.7 });
      }
      s.add(geo.sphere(0.07, 6, 5), p.accent, { x, y: y + 0.03, z });
    }
    return s.finish();
  },

  /** Citrouille : 5 lobes aplatis (main / secondary), tige et vrille accent (vert), grimace discrete sur la variante 0. */
  pumpkin: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const lobes = 5;
    for (let i = 0; i < lobes; i++) {
      const a = (i / lobes) * TAU + 0.3;
      s.add(geo.sphere(1, 10, 8), i % 2 === 0 ? p.main : p.secondary, { x: Math.sin(a) * 0.2, y: 0.38, z: Math.cos(a) * 0.2, sx: 0.32, sy: 0.4, sz: 0.32, ry: a });
    }
    s.add(geo.sphere(1, 12, 8), p.main, { y: 0.38, sx: 0.4, sy: 0.4, sz: 0.4 });
    s.add(geo.cylinder(0.06, 0.09, 0.2, 6), p.accent, { x: 0.02, y: 0.82, z: 0, rz: -0.2 });
    s.add(geo.torus(0.07, 0.02), p.accent, { x: 0.12, y: 0.76, z: 0.04, rx: 1.2 });
    s.add(geo.ico(1, 0), p.accent, { x: -0.16, y: 0.7, z: 0.02, sx: 0.12, sy: 0.04, sz: 0.07, rz: 0.4 });
    if (variant % 3 === 0) {
      for (const sx of [-1, 1]) s.add(geo.cone(0.06, 0.1, 3), EYE, { x: sx * 0.13, y: 0.46, z: 0.37, rx: Math.PI / 2 });
      s.add(geo.box(0.2, 0.04, 0.05), EYE, { y: 0.28, z: 0.4 });
    }
    void rand;
    return s.finish();
  },

  /** Tas de feuilles d'automne : monticule mou et plat, feuilles ovales empilees (main / secondary / accent). Rayon ~0.7. */
  leaves: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.ico(1, 1), p.secondary, { y: 0.12, sx: 0.62, sy: 0.2, sz: 0.58, ry: rand() * 3 });
    const n = 7 + (variant % 3);
    const cols = [p.main, p.secondary, p.accent];
    for (let i = 0; i < n; i++) {
      const a = rand() * TAU;
      const d = rand() * 0.5;
      const r = 0.2 + 0.1 * rand();
      s.add(geo.sphere(1, 8, 5), cols[i % 3] ?? p.main, { x: Math.sin(a) * d, y: 0.18 + 0.12 * (1 - d / 0.5) + rand() * 0.04, z: Math.cos(a) * d, sx: r, sy: 0.05, sz: r * 0.6, ry: rand() * TAU, rx: (rand() - 0.5) * 0.4, rz: (rand() - 0.5) * 0.4 });
    }
    for (let i = 0; i < 3; i++) {
      const a = rand() * TAU;
      s.add(geo.sphere(1, 8, 5), cols[(i + 1) % 3] ?? p.main, { x: Math.sin(a) * 0.65, y: 0.04, z: Math.cos(a) * 0.65, sx: 0.16, sy: 0.04, sz: 0.1, ry: rand() * TAU });
    }
    return s.finish();
  },

  /** Bonhomme de neige : 3 boules (main), nez carotte (accent), yeux / boutons sombres, echarpe rouge, bras de brindilles ; chapeau selon variante. */
  snowman: ({ palette: p, variant, geo }) => {
    const s = new Shape();
    s.add(geo.sphere(1, 14, 10), p.main, { y: 0.5, s: 0.52 });
    s.add(geo.sphere(1, 14, 10), mixHex(p.main, p.secondary, 0.25), { y: 1.2, s: 0.38 });
    s.add(geo.sphere(1, 14, 10), p.main, { y: 1.74, s: 0.28 });
    s.add(geo.cone(0.06, 0.28, 6), p.accent, { y: 1.72, z: 0.32, rx: Math.PI / 2 });
    for (const sx of [-1, 1]) s.add(geo.sphere(0.04, 6, 5), EYE, { x: sx * 0.1, y: 1.82, z: 0.25 });
    for (let i = 0; i < 3; i++) s.add(geo.sphere(0.04, 6, 5), EYE, { y: 1.35 - i * 0.16, z: 0.37 - i * 0.02 });
    const scarf = '#e5525f';
    s.add(geo.torus(0.3, 0.07), scarf, { y: 1.5, rx: Math.PI / 2 });
    s.add(geo.box(0.1, 0.34, 0.06), scarf, { x: 0.2, y: 1.32, z: 0.26, rz: 0.15 });
    for (const sx of [-1, 1]) limb(s, geo, [sx * 0.34, 1.22, 0], [sx * 0.72, 1.4, 0.04], 0.03, TRUNK);
    if (variant % 2 === 0) {
      s.add(geo.cylinder(0.2, 0.2, 0.04, 12), EYE, { y: 2.0 });
      s.add(geo.cylinder(0.14, 0.14, 0.2, 12), EYE, { y: 2.12 });
      s.add(geo.cylinder(0.145, 0.145, 0.04, 12), scarf, { y: 2.05 });
    }
    return s.finish();
  },

  /** Herbe a chat : bouquet de 3 a 4 tiges courbes, feuilles dentelees, epis violets (accent) lumineux (glow) ; reperable de loin. */
  catnip: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const glow = p.glow ?? p.accent;
    const stems = 3 + (variant % 2);
    s.add(geo.sphere(1, 10, 6), p.secondary, { y: 0.05, sx: 0.32, sy: 0.08, sz: 0.3 });
    for (let i = 0; i < stems; i++) {
      const a = (i / stems) * TAU + rand() * 0.8;
      const lean = 0.12 + 0.12 * rand();
      const h = 0.75 + 0.35 * rand();
      const tx = Math.sin(a) * lean * h;
      const tz = Math.cos(a) * lean * h;
      limb(s, geo, [0, 0.05, 0], [tx, h, tz], 0.035, i % 2 === 0 ? p.main : p.secondary);
      for (let k = 0; k < 3; k++) {
        const t = 0.25 + k * 0.22;
        const side = k % 2 === 0 ? 1 : -1;
        s.add(geo.sphere(1, 8, 5), k % 2 === 0 ? p.main : p.secondary, { x: tx * t + Math.cos(a) * side * 0.1, y: 0.05 + (h - 0.05) * t, z: tz * t - Math.sin(a) * side * 0.1, sx: 0.12, sy: 0.04, sz: 0.07, ry: a, rz: side * 0.5 });
      }
      // epi violet (accent) : 3 boules empilees, pointe lumineuse (glow) qui scintille
      for (let k = 0; k < 3; k++) {
        const r = 0.13 - k * 0.025;
        s.add(geo.sphere(r, 8, 6), k === 1 ? mixHex(p.accent, '#8f5fe0', 0.35) : p.accent, { x: tx, y: h + 0.03 + k * 0.12, z: tz });
      }
      s.add(geo.sphere(0.06, 6, 5), glow, { x: tx, y: h + 0.4, z: tz }, glow);
    }
    return s.finish();
  },
};
